import { useCallback, useState } from "react";
import styles from "../SettingsPage.module.css";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";
import { useUserSkillPrefs } from "../../shared/userSkillPrefs";
import { ChevronDownIcon, ChevronRightIcon } from "../../shared/components/icons";
import type { Channel } from "../../shared/host";
import type { StudioSkill } from "../../services/studio/types";

/**
 * Per-skill enable/disable for the signed-in user (ST-04), rendered from the
 * `skillGroups` the settings endpoint returns (`GET /api/{channel}/settings`).
 *
 * Two layers decide a skill's effective state, and the persona's grant is the
 * ceiling (ST-06 — the user hides within the granted set, never surfaces):
 *   - `skill.enabled` (PersonaSkills.Enabled, set in Studio) — when false the
 *     row renders present-and-off and is NOT user-toggleable; re-enabling is
 *     the administrator's call. A skill the persona was never granted has no
 *     row at all: no grant means "not yours", not "temporarily off".
 *   - the user's own disable list (useUserSkillPrefs, localStorage per host) —
 *     toggleable here, honoured by the slash registry (the `/?` palette and
 *     `/keyword` firing) and sent with chat requests so the router never
 *     selects a disabled skill.
 *
 * Rows lacking a `skillKey` (backend predating the field) stay read-only —
 * there is nothing stable to key the preference on.
 */

interface SkillsTabProps {
  readonly host: Channel | null;
}

/** A skill the user may toggle: granted by the persona and carrying a key. */
function isUserTogglable(s: StudioSkill): s is StudioSkill & { skillKey: string } {
  return s.enabled && typeof s.skillKey === "string" && s.skillKey.length > 0;
}

export function SkillsTab({ host }: Readonly<SkillsTabProps>) {
  const appConfig = useOptionalAppConfig();
  const data = appConfig?.data ?? null;
  const status = appConfig?.status ?? "idle";
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  const { isSkillDisabled, toggleSkill, setSkillsDisabled } = useUserSkillPrefs(host);

  const expandGroup = useCallback((groupId: number) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  if (status === "loading" || status === "idle") {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Skills</h3>
        <p className={styles.placeholder}>Loading…</p>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Skills</h3>
        <p className={styles.placeholder}>
          Could not load skills. Pull-to-refresh or try again later.
        </p>
      </div>
    );
  }

  const groups = data.skillGroups;
  if (groups.length === 0) {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Skills</h3>
        <div className={styles.infoBanner}>
          No skills are available for your persona in this channel.
        </div>
      </div>
    );
  }

  // Effective = granted by the persona AND not switched off by the user.
  const isEffectivelyOn = (s: StudioSkill) =>
    s.enabled && !(isUserTogglable(s) && isSkillDisabled(s.skillKey));

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Skills</h3>
        <div className={styles.infoBanner}>
          Your administrator decides which skills are available. Open a group
          to switch individual skills off — a skill you switch off is hidden
          from /? and stops acting for you. Only you are affected, and you can
          switch it back on any time.
        </div>

        {groups.map((g) => {
          const total = g.skills.length;
          // Derived rather than sent on the wire, so it can never disagree with
          // the array it describes.
          const enabled = g.skills.filter(isEffectivelyOn).length;
          const isOpen = !!openGroups[g.id];
          const togglable = g.skills.filter(isUserTogglable);
          const anyOn = togglable.some((s) => !isSkillDisabled(s.skillKey));
          // "All on" / "2 of 3 on" / "Off" — readable without expanding the
          // group, which is the point of the header on a workspace with dozens
          // of skills.
          let stateLabel: string;
          if (enabled === total) stateLabel = "All on";
          else if (enabled === 0) stateLabel = "Off";
          else stateLabel = `${enabled} of ${total} on`;

          return (
            <div key={g.id} className={styles.row} style={{ display: "block" }}>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  onClick={() => expandGroup(g.id)}
                  aria-expanded={isOpen}
                  style={{
                    flex: 1,
                    padding: 0,
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {/* Expand affordance — the only visual cue that the group
                      drills into per-skill rows, so it must always render.
                      The icons carry no intrinsic size; .chevron sizes the svg. */}
                  <span aria-hidden="true" className={styles.chevron}>
                    {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </span>
                  <span className={styles.rowInfo}>
                    <span className={styles.rowLabel} style={{ display: "block" }}>
                      {g.name}
                    </span>
                    <span className={styles.rowDesc}>
                      {stateLabel} · {total} {total === 1 ? "skill" : "skills"}
                    </span>
                  </span>
                </button>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    role="switch"
                    aria-label={`${g.name} group status`}
                    checked={enabled > 0}
                    // Bulk on/off for the skills the user may control. A group
                    // whose every skill is persona-disabled (or unkeyed) has
                    // nothing to act on, so its switch stays locked — handler
                    // included, so a synthetic click can't write an empty list.
                    disabled={togglable.length === 0}
                    onChange={
                      togglable.length > 0
                        ? () =>
                            setSkillsDisabled(
                              togglable.map((s) => s.skillKey),
                              anyOn,
                            )
                        : undefined
                    }
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>

              {isOpen && (
                <div style={{ marginTop: 8 }}>
                  {g.skills.map((s) => {
                    const togglableSkill = isUserTogglable(s);
                    return (
                      <div
                        key={s.id}
                        className={styles.row}
                        // Aligns under the group label (chevron 16px + 8px gap).
                        style={{ paddingLeft: 24, borderBottom: 0 }}
                      >
                        <div className={styles.rowInfo}>
                          <div className={styles.rowLabel}>{s.name}</div>
                          <div className={styles.rowDesc}>
                            {s.enabled ? s.type : `${s.type} · turned off by your administrator`}
                          </div>
                        </div>
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            role="switch"
                            aria-label={`${s.name} status`}
                            checked={isEffectivelyOn(s)}
                            // Persona-disabled (admin ceiling) and unkeyed rows
                            // stay locked: the first is the administrator's
                            // decision, the second has no stable key to store.
                            disabled={!togglableSkill}
                            onChange={togglableSkill ? () => toggleSkill(s.skillKey) : undefined}
                          />
                          <span className={styles.toggleSlider} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
